/**
 * Plan Action - WHAT Section
 *
 * Edit mode UI for configuring plan assignment.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-plan.hbs
 *
 * Fields:
 * - reminderOnId: Selected plan ID
 * - planDay: Day to import to calendar (empty = assign only, or day of week)
 * - defaultLifecycleStageId: Optional lifecycle stage to transition to
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';
import { LifecycleTransitionField } from '../components';

/**
 * Plan WHAT Section - Edit mode
 */
export const PlanWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available plans
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['workout-plans', 'options'],
    queryFn: fetchWorkoutPlanOptions,
    staleTime: 60000,
  });

  const planOptions = plans.map((plan) => ({
    value: plan.id.toString(),
    label: plan.name,
  }));

  const planDayOptions = [
    { value: '', label: t('automations.planDays.onlyAssign') },
    { value: 'current', label: t('automations.planDays.currentDay') },
    { value: 'sunday', label: t('automations.planDays.sunday') },
    { value: 'monday', label: t('automations.planDays.monday') },
    { value: 'tuesday', label: t('automations.planDays.tuesday') },
    { value: 'wednesday', label: t('automations.planDays.wednesday') },
    { value: 'thursday', label: t('automations.planDays.thursday') },
    { value: 'friday', label: t('automations.planDays.friday') },
    { value: 'saturday', label: t('automations.planDays.saturday') },
  ];

  // Check if selected plan is a flex plan (flex plans don't have calendar import)
  const selectedPlan = plans.find(
    (p) => p.id.toString() === automation.reminderOnId?.toString()
  );
  const isFlex = selectedPlan?.planType === 'flex';

  return (
    <Stack spacing={2}>
      {/* Plan Selection */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.plan.assignTo')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={planOptions}
            prompt={plansLoading ? t('automations.common.loading') : t('automations.plan.selectPlan')}
            onChange={(e) => {
              const planId = e.target.value;
              setFieldValue(`automations.${index}.reminderOnId`, planId);
              // If selecting a flex plan, clear planDay
              const plan = plans.find((p) => p.id.toString() === planId);
              if (plan?.planType === 'flex') {
                setFieldValue(`automations.${index}.planDay`, '');
              }
            }}
          />
        </Box>
      </Box>

      {/* Calendar Import Day - only show for non-flex plans */}
      {!isFlex && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box>{t('automations.plan.importIntoCalendarOn')}</Box>
          <Box sx={{ minWidth: 180 }}>
            <FormSelect
              name={`automations.${index}.planDay`}
              options={planDayOptions}
            />
          </Box>
        </Box>
      )}

      {/* Lifecycle Stage Transition (Optional) */}
      <LifecycleTransitionField
        name={`automations.${index}.defaultLifecycleStageId`}
      />
    </Stack>
  );
};

/**
 * Plan What Summary Component
 * Fetches plan data to display the actual plan name
 */
export const PlanWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: plans = [] } = useQuery({
    queryKey: ['workout-plans', 'options'],
    queryFn: fetchWorkoutPlanOptions,
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.plan.noPlanSelected')}</i>;
  }

  const selectedPlan = plans.find(
    (p) => p.id.toString() === automation.reminderOnId?.toString()
  );
  const planName = selectedPlan?.name || `Plan #${automation.reminderOnId}`;

  const planDay = String(automation.planDay || '');
  const dayKeyMap: Record<string, string> = {
    '': 'onlyAssign',
    'current': 'currentDay',
    'sunday': 'sunday',
    'monday': 'monday',
    'tuesday': 'tuesday',
    'wednesday': 'wednesday',
    'thursday': 'thursday',
    'friday': 'friday',
    'saturday': 'saturday',
  };
  const dayKey = dayKeyMap[planDay] || 'onlyAssign';
  const dayText = t(`automations.planDays.${dayKey}`);

  if (planDay) {
    return (
      <>
        {planName} - {t('automations.plan.importOn', { day: dayText })}
      </>
    );
  }
  return (
    <>
      {planName} - {dayText}
    </>
  );
};
